import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import { Skeleton } from '../../../components/Skeleton';
import type { Insight } from '../../../domain/insight';
import { InsightType } from '../../../domain/insightType';

import {
  StyledEmailCard,
  StyledEmailCardHeader,
  StyledEmailIcon,
  StyledEmailSection,
  StyledGraphSkeleton,
  StyledSkeleton,
  StyledSkeletonGroup,
} from './styles';

const getGraphHeight = (insight: Insight) => {
  if (insight.insightType === InsightType.Opportunity) {
    const baseHeight = 116;
    const competitorsCount = insight.competitors?.filter((c) => !!c.value).length ?? 0;
    const competitorHeight = 16;
    const gap = 4;
    const competitorsSectionHeight = competitorsCount * competitorHeight + (competitorsCount - 1) * gap;
    return `${competitorsSectionHeight + baseHeight}px`;
  }

  return '225px';
};

export type EmailPreviewLoadingProps = {
  footer: ReactNode;
  shouldAnimate: boolean;
  insight: Insight;
};

export const EmailPreviewLoading = ({ footer, shouldAnimate, insight }: EmailPreviewLoadingProps) => {
  const emailCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!emailCardRef.current || !shouldAnimate) {
      return;
    }
    const cardElement = emailCardRef.current;

    const keyframes = [{ maxHeight: '0px' }, { maxHeight: '280px' }];
    const animation = cardElement.animate(keyframes, { duration: 200, easing: 'ease-out' });
    animation.onfinish = () => {
      cardElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
  }, []);

  return (
    <StyledEmailCard ref={emailCardRef} data-automation="EmailLoadingCard">
      <StyledEmailCardHeader>
        <StyledEmailIcon iconName="sparkles-gradient" size="xs" />
        <StyledSkeleton height="16px" width="235px" />
      </StyledEmailCardHeader>
      <StyledEmailSection>
        <StyledSkeletonGroup>
          <Skeleton height="18px" width="205px" />
          <Skeleton height="18px" width="184px" />
          <Skeleton height="18px" width="172px" />
        </StyledSkeletonGroup>

        <StyledSkeletonGroup>
          <Skeleton height="18px" width="184px" />
          <Skeleton height="18px" width="205px" />
          <Skeleton height="18px" width="172px" />
        </StyledSkeletonGroup>

        <StyledGraphSkeleton width="100%" height={getGraphHeight(insight)} />
        {footer}
      </StyledEmailSection>
    </StyledEmailCard>
  );
};
